import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ThirdPartyLogo from '../components/ThirdPartyLogo';
import { BRAND_FONT_STACK } from '../constants/thirdPartyPages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Rent-Free Living Certification — property-owner public form. The owner
// counterpart to VORForm/VOMForm. Posts to /api/rent-free/submit/:token.
export default function RentFreeForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const sigCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [borrower, setBorrower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [form, setForm] = useState({
    owner_name: '', owner_address: '', owner_phone: '', owner_email_on_form: '',
    relationship: '', property_address: '', occupancy_start_date: '',
    confirms_rent_free: false, owner_signature: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!token) { setError('Missing verification token.'); setLoading(false); return; }
    fetch(`${API_URL}/api/rent-free/validate/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error === 'used') { setSubmitted(true); setLoading(false); return; }
        if (!data.valid) { setError(data.error === 'expired' ? 'This verification link has expired.' : 'Invalid verification link.'); setLoading(false); return; }
        setBorrower(data);
        setForm(p => ({
          ...p,
          owner_name: data.ownerName || '',
          owner_email_on_form: data.ownerEmail || '',
          property_address: data.propertyAddress || '',
        }));
        setLoading(false);
      })
      .catch(() => { setError('Could not load verification form.'); setLoading(false); });
  }, [token]);

  const startDraw = (e) => {
    setIsDrawing(true);
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches?.[0] || e).clientX - rect.left) * scaleX;
    const y = ((e.touches?.[0] || e).clientY - rect.top) * scaleY;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches?.[0] || e).clientX - rect.left) * scaleX;
    const y = ((e.touches?.[0] || e).clientY - rect.top) * scaleY;
    ctx.lineWidth = 2; ctx.strokeStyle = '#111827'; ctx.lineCap = 'round';
    ctx.lineTo(x, y); ctx.stroke();
  };
  const endDraw = () => {
    setIsDrawing(false);
    const sig = sigCanvasRef.current?.toDataURL('image/png');
    setForm(p => ({ ...p, owner_signature: sig }));
  };
  const clearSig = () => {
    const canvas = sigCanvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setForm(p => ({ ...p, owner_signature: '' }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!form.owner_address.trim()) errs.owner_address = 'This field is required.';
    if (!form.owner_phone.trim()) errs.owner_phone = 'This field is required.';
    if (!form.relationship.trim()) errs.relationship = 'This field is required.';
    setFieldErrors(errs);
    if (Object.keys(errs).length) { setSubmitError('Please fill in all required fields.'); return; }
    if (!form.confirms_rent_free) { setSubmitError('Please confirm the resident lives at the property rent-free.'); return; }
    if (!form.owner_signature) { setSubmitError('Please sign the form.'); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/api/rent-free/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) setSubmitted(true);
      else setSubmitError(data.error || 'Submission failed.');
    } catch { setSubmitError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: BRAND_FONT_STACK, padding: '32px 16px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <ThirdPartyLogo height={36} />
        </div>
        {children}
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#9ca3af' }}>
          Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
        </div>
      </div>
    </div>
  );

  if (loading) return wrap(<div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading...</div>);

  if (error) return wrap(
    <div style={{ background: '#fff', borderRadius: '16px', padding: '48px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Link Invalid</div>
      <div style={{ fontSize: '14px', color: '#9ca3af' }}>{error}</div>
    </div>
  );

  if (submitted) return wrap(
    <div style={{ background: '#fff', borderRadius: '16px', padding: '48px 40px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '3px solid #3a6ad9' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>
      </div>
      <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>Certification Submitted</h2>
      <p style={{ margin: 0, fontSize: '15px', color: '#6b7280', lineHeight: '1.6' }}>
        Thank you! Your rent-free living certification has been submitted to Go Doorly, LLC and will be reviewed as part of the loan process.
      </p>
    </div>
  );

  const inputStyle = { width: '100%', padding: '11px 13px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '15px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fff' };
  const labelStyle = { fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const sectionStyle = { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6', marginTop: '4px' };

  return wrap(
    <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
      <div style={{ background: '#3a6ad9', padding: '24px 28px' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '800', color: '#fffd5b' }}>Rent-Free Living Certification</h1>
        <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.75)' }}>
          Please complete all fields accurately. This form will be submitted directly to Go Doorly, LLC.
        </p>
      </div>

      {borrower && (
        <div style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', padding: '14px 28px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '13px', color: '#374151' }}>
            <span style={{ fontWeight: '700' }}>Resident: </span>{borrower.borrowerFullName}
          </div>
          {borrower.loanNumber && (
            <div style={{ fontSize: '13px', color: '#374151' }}>
              <span style={{ fontWeight: '700' }}>Loan #: </span>{borrower.loanNumber}
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={sectionStyle}>Property Owner Information</div>

          <div>
            <label style={labelStyle}>Full Name <span style={{ color: '#ff082f' }}>*</span></label>
            <input style={inputStyle} value={form.owner_name} onChange={e => setForm(p => ({ ...p, owner_name: e.target.value }))} placeholder="Your full legal name" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Mailing Address <span style={{ color: '#ff082f' }}>*</span></label>
              <input
                style={{ ...inputStyle, borderColor: fieldErrors.owner_address ? '#ff082f' : '#d1d5db' }}
                value={form.owner_address}
                onChange={e => { setForm(p => ({ ...p, owner_address: e.target.value })); if (fieldErrors.owner_address) setFieldErrors(er => ({ ...er, owner_address: '' })); }}
                placeholder="123 Main St, Austin, TX 78701"
              />
              {fieldErrors.owner_address && (<div style={{ fontSize: '11px', color: '#ff082f', marginTop: '4px', fontWeight: '600' }}>{fieldErrors.owner_address}</div>)}
            </div>
            <div>
              <label style={labelStyle}>Phone Number <span style={{ color: '#ff082f' }}>*</span></label>
              <input
                style={{ ...inputStyle, borderColor: fieldErrors.owner_phone ? '#ff082f' : '#d1d5db' }}
                value={form.owner_phone}
                onChange={e => { setForm(p => ({ ...p, owner_phone: e.target.value })); if (fieldErrors.owner_phone) setFieldErrors(er => ({ ...er, owner_phone: '' })); }}
                placeholder="512-555-0100"
              />
              {fieldErrors.owner_phone && (<div style={{ fontSize: '11px', color: '#ff082f', marginTop: '4px', fontWeight: '600' }}>{fieldErrors.owner_phone}</div>)}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Email Address</label>
            <input type="email" style={inputStyle} value={form.owner_email_on_form} onChange={e => setForm(p => ({ ...p, owner_email_on_form: e.target.value }))} placeholder="owner@example.com" />
          </div>

          <div>
            <label style={labelStyle}>Relationship to Resident <span style={{ color: '#ff082f' }}>*</span></label>
            <input
              style={{ ...inputStyle, borderColor: fieldErrors.relationship ? '#ff082f' : '#d1d5db' }}
              value={form.relationship}
              onChange={e => { setForm(p => ({ ...p, relationship: e.target.value })); if (fieldErrors.relationship) setFieldErrors(er => ({ ...er, relationship: '' })); }}
              placeholder="e.g. Parent, Family member, Friend"
            />
            {fieldErrors.relationship && (<div style={{ fontSize: '11px', color: '#ff082f', marginTop: '4px', fontWeight: '600' }}>{fieldErrors.relationship}</div>)}
          </div>

          <div style={sectionStyle}>Occupancy</div>

          <div>
            <label style={labelStyle}>Property Address <span style={{ color: '#ff082f' }}>*</span></label>
            <input style={inputStyle} value={form.property_address} onChange={e => setForm(p => ({ ...p, property_address: e.target.value }))} placeholder="Full property address" />
          </div>

          <div>
            <label style={labelStyle}>Occupancy Start Date</label>
            <input type="date" style={inputStyle} value={form.occupancy_start_date} onChange={e => setForm(p => ({ ...p, occupancy_start_date: e.target.value }))} />
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', background: '#f9fafb', borderRadius: '10px', padding: '14px 16px' }}>
            <input type="checkbox" checked={form.confirms_rent_free} onChange={e => setForm(p => ({ ...p, confirms_rent_free: e.target.checked }))} style={{ marginTop: '3px', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>
              I certify that the resident named above lives at this property and <strong>does not pay rent</strong>.
            </span>
          </label>

          <div style={sectionStyle}>Property Owner Signature</div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Sign Below <span style={{ color: '#ff082f' }}>*</span></label>
              <button onClick={clearSig} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>Clear</button>
            </div>
            <canvas ref={sigCanvasRef} width={544} height={120}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
              style={{ width: '100%', height: '120px', border: '2px solid #d1d5db', borderRadius: '10px', cursor: 'crosshair', touchAction: 'none', background: '#fafafa' }} />
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Draw your signature using your mouse or finger</div>
          </div>

          <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px 16px', fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
            By signing above, I certify that all information provided is true and accurate to the best of my knowledge, and I understand it will be used in connection with a mortgage loan application processed by Go Doorly, LLC.
          </div>

          {submitError && (
            <div style={{ background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#ff082f', fontWeight: '600' }}>{submitError}</div>
          )}

          <button onClick={handleSubmit} disabled={submitting || !form.owner_signature}
            style={{
              width: '100%', padding: '16px',
              background: submitting || !form.owner_signature ? '#e5e7eb' : '#3a6ad9',
              color: submitting || !form.owner_signature ? '#9ca3af' : '#ffffff',
              border: 'none', borderRadius: '12px',
              cursor: submitting || !form.owner_signature ? 'not-allowed' : 'pointer',
              fontWeight: '800', fontSize: '16px',
            }}>
            {submitting ? 'Submitting...' : 'Submit Certification'}
          </button>
        </div>
      </div>
    </div>
  );
}
